import { notification, UploadFile } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { t } from '../i18n';
import {
  PaginatedResponse,
  JobPostingDetail,
  QuestionCollection,
  RecruiterCollection,
  ExpandedStageCollection,
} from '../types';
import useAllowedUsersMutation from './useAllowedUsersMutation';
import useCreateJobPostingMutation from './useCreateJobPostingMutation';
import useDescriptionMutation from './useDescriptionMutation';
import useProfileMutation from './useProfileMutation';
import usePublicationMutation from './usePublicationMutation';
import useStagesMutation from './useStagesMutation';

interface MutationInput {
  jobPostingId: JobPostingDetail['id'];
}

type CloneMutationOptions = Omit<
  UseMutationOptions<JobPostingDetail, AxiosError<unknown, unknown>, MutationInput, unknown>,
  'mutationFn'
>;

const useCloneJobPostingMutation = (options?: CloneMutationOptions) => {
  const { onError, ...otherOptions } = options ?? {};
  const queryClient = useQueryClient();

  const createJobPostingMutation = useCreateJobPostingMutation();
  const profileMutation = useProfileMutation();
  const stagesMutation = useStagesMutation();
  const descriptionMutation = useDescriptionMutation();
  const publicationMutation = usePublicationMutation();
  const allowedUsersMutation = useAllowedUsersMutation();

  return useAPIMutation<JobPostingDetail, MutationInput, unknown>(
    async ({ jobPostingId }: MutationInput, api) => {
      const jobPosting = await api.get(`/job-postings/${jobPostingId}/`);

      let stages: ExpandedStageCollection[] = [];
      let stagesPage: PaginatedResponse<ExpandedStageCollection> = {
        next: undefined,
        count: 0,
        results: [],
      };

      do {
        stagesPage = await queryClient.fetchQuery(
          stagesPage.next ?? [
            `/stages/`,
            {
              params: {
                job_posting_id: jobPosting.data.id,
                expand: 'interview_script_content,email_communication_template',
              },
            },
          ]
        );

        stages = stages.concat(stagesPage.results);
      } while (stagesPage.next);

      if (stages.some((stage) => stage.sub_type === 'LEGACY' && stage.type === 'CUSTOM')) {
        throw new Error('job_posting_has_legacy_stages');
      }

      const newJobPosting = await createJobPostingMutation.mutateAsync({
        positions: jobPosting.data.positions,
        name: `${jobPosting.data.name} (${t('Cópia')})`,
        startSalaryRange: jobPosting.data.start_salary_range || undefined,
        endSalaryRange: jobPosting.data.end_salary_range || undefined,
        limitDays: jobPosting.data.limit_days || undefined,
        seniorityLevel: jobPosting.data.seniority_level || undefined,
        workModel: jobPosting.data.work_model,
        hireModel: jobPosting.data.hire_model,
      });

      let recruiters: RecruiterCollection[] = [];
      let recruitersPage: Pick<AxiosResponse<PaginatedResponse<RecruiterCollection>>, 'data'> = {
        data: {
          next: undefined,
          count: 0,
          results: [],
        },
      };
      let url = '/recruiters/';
      let params: object = {
        params: {
          job_posting_id: jobPosting.data.id,
        },
      };

      do {
        recruitersPage = await api.get(url, params);

        recruiters = recruiters.concat(recruitersPage.data.results);

        url = recruitersPage.data?.next || '';
        params = {};
      } while (recruitersPage.data?.next);

      await allowedUsersMutation.mutateAsync({
        jobPostingId: newJobPosting.data?.id,
        related: recruiters
          .filter((recruiter) => recruiter.type === 'RELATED')
          .map(({ user }) => user.id),
        manager: recruiters.find(({ type }) => type === 'MANAGER')?.user.id,
        recruiter: recruiters.find(({ type }) => type === 'RECRUITER')?.user.id,
        observers: recruiters.filter(({ type }) => type === 'OBSERVER')?.map(({ user }) => user.id),
      });

      if (jobPosting.data.function_id) {
        await profileMutation.mutateAsync({
          jobPostingId: newJobPosting.data?.id,
          functionId: jobPosting.data.function_id,
        });
      }

      await descriptionMutation.mutateAsync({
        jobPostingId: newJobPosting.data?.id,
        description: jobPosting.data.description,
        competencies: [],
      });

      await publicationMutation.mutateAsync({
        jobPostingId: newJobPosting.data?.id,
        timezone: jobPosting.data.timezone || 'America/Sao_Paulo',
        description: jobPosting.data.description,
        externalPublication: false,
        workModel: jobPosting.data.work_model,
        hireModel: jobPosting.data.hire_model,
        country: jobPosting.data.country,
        state: jobPosting.data.state,
        city: jobPosting.data.city,
        googleForJobsExternalPublication: false,
        netvagasExternalPublication: false,
        externalPublicationPeriod: null,
        linkedinExternalPublication: false,
        careerjetExternalPublication: false,
        joobleExternalPublication: false,
        unlistedExternalPublication: false,
        competencies: [],
      });

      let questions: QuestionCollection[] = [];
      let questionsPage: PaginatedResponse<QuestionCollection> = {
        count: 0,
        next: undefined,
        results: [],
      };

      do {
        questionsPage = await queryClient.fetchQuery(
          questionsPage.next
            ? questionsPage.next
            : ['/questions/', { params: { job_posting_id: jobPostingId } }]
        );

        questions = questions.concat(questionsPage.results);
      } while (questionsPage.next);

      await stagesMutation.mutateAsync({
        jobPostingId: newJobPosting.data.id,
        stages: stages.map((stage) => ({
          type: stage.type,
          sub_type: stage.sub_type,
          name: stage.name,
          fixed: stage.fixed,
          sequence: stage.sequence,
          send_tests: stage.send_tests,
          testTypeList: stage.tests,
          interview_script_template: stage.interview_script_template,
          interview_script_name: '',
          interview_script_content: stage.interview_script_content,
          lock_interview_script: false,
          ...(stage.type === 'SUBSCRIBED'
            ? {
                email_communication_template:
                  stage.email_communication_template &&
                  !stage.email_communication_template.individual
                    ? stage.email_communication_template.id
                    : null,
                subject: stage.email_communication_template?.subject || '',
                message: stage.email_communication_template?.message || '',
                fromName: stage.email_communication_template?.from_name || '',
                attachments:
                  stage.email_communication_template?.attachments?.map<UploadFile>((id) => ({
                    name: `${id}`,
                    uid: `${id}`,
                  })) || [],
                questions: questions.map((question) => ({
                  type: question.type,
                  options: question.alternatives?.map((alternative) => ({
                    description: alternative.description,
                  })),
                  description: question.description,
                  required: question.required,
                })),
              }
            : stage.sub_type === 'PRACTICAL_TECHNICAL_CASE' &&
              stage.email_communication_template?.individual
            ? {
                email_communication_template: 0,
                subject: stage.email_communication_template?.subject || '',
                message: stage.email_communication_template?.message || '',
                fromName: stage.email_communication_template?.from_name || '',
                attachments:
                  stage.email_communication_template?.attachments?.map<UploadFile>((id) => ({
                    name: `${id}`,
                    uid: `${id}`,
                  })) || [],
                interview_script_template: 0,
                interview_script_content: stage.interview_script_content || '',
              }
            : {
                email_communication_template: stage.email_communication_template?.id || null, //FIXME: talvez seja melhor verificar para cada etapa o template se é individual ou não
                subject: '',
                message: '',
                fromName: '',
                attachments: [],
              }),
        })),
      });

      return newJobPosting.data;
    },
    {
      onError(error, variables, context) {
        if (error.message === 'job_posting_has_legacy_stages') {
          notification.error({
            duration: 0,
            message: t('Erro ao clonar vaga'),
            description: t(
              'A vaga possui etapas que não tiveram o "Tipo de Etapa" selecionado ainda. Por favor, edite as etapas e coloque os respectivos tipos.'
            ),
          });
          return;
        }
        if (onError) {
          return onError(error, variables, context);
        }
        return queryClient.defaultMutationOptions().onError?.(error, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/job-postings/');
      },
      ...otherOptions,
    }
  );
};

export default useCloneJobPostingMutation;
